import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { clearDraft } from "../lib/registerDraft";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, Loader2, AlertOctagon } from "lucide-react";

const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 24; // ~60 seconds total

const RegistrationSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { setUserFromToken } = useAuth() || {};
  const [phase, setPhase] = useState("verifying"); // verifying | ready | failed | timeout
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setPhase("failed");
      setError("Missing checkout session. Please retry registration.");
      return;
    }
    let cancelled = false;
    const tick = async () => {
      pollsRef.current += 1;
      try {
        const r = await api.get(`/auth/check-registration?session_id=${encodeURIComponent(sessionId)}`);
        if (cancelled) return;
        const { access_token, user: u } = r.data || {};
        if (access_token && u) {
          // AuthContext.setUserFromToken owns token persistence; write
          // localStorage directly only when it's unavailable or throws.
          if (typeof setUserFromToken === "function") {
            try { await setUserFromToken(access_token, u); } catch (e) {
              console.warn("RegistrationSuccessPage: setUserFromToken failed", e);
              localStorage.setItem("token", access_token);
            }
          } else {
            localStorage.setItem("token", access_token);
          }
          setUser(u);
          setPhase("ready");
          // Account exists — drop the register-page draft so a later visit
          // to /register starts clean (CAPMARKETS-066).
          clearDraft();
          return;
        }
        // Empty token shape (shouldn't happen — backend returns 402 for pending) — retry
        if (pollsRef.current < MAX_POLLS) setTimeout(tick, POLL_INTERVAL_MS);
        else setPhase("timeout");
      } catch (e) {
        if (cancelled) return;
        const status = e.response?.status;
        if (status === 402) {
          // payment-pending — keep polling
          if (pollsRef.current < MAX_POLLS) setTimeout(tick, POLL_INTERVAL_MS);
          else setPhase("timeout");
          return;
        }
        if (status === 404) {
          setPhase("failed");
          setError("This checkout session was not found. Please retry registration.");
          return;
        }
        setPhase("failed");
        setError(e.response?.data?.detail || "We couldn't confirm your payment. Please contact admin@echosensecapital.com.");
      }
    };
    tick();
    return () => { cancelled = true; };
    // `setUserFromToken` from AuthContext is referenced but we only want
    // the effect to re-trigger when `sessionId` changes (the actual
    // identity of the polling target). Module-level constants
    // (POLL_INTERVAL_MS, MAX_POLLS) are not stateful and need not be deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-4 mb-8">
          <img src="/logo.png" alt="EchoSense Capital Club" className="w-16 h-16 object-contain" />
          <div className="text-center">
            <span className="text-3xl font-semibold text-slate-900 font-serif">EchoSense Capital Club</span>
          </div>
        </Link>

        {phase === "verifying" && (
          <Card className="bg-surface border-platinum/10">
            <CardHeader className="text-center">
              <Loader2 className="mx-auto text-blue-600 animate-spin mb-2" size={32} />
              <CardTitle className="text-2xl font-serif text-slate-900">Confirming your membership…</CardTitle>
              <CardDescription className="text-platinum/60">
                Stripe is finalizing your $99 registration fee. Please don't close this window.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center text-sm text-platinum/70">
              This typically takes a few seconds.
            </CardContent>
          </Card>
        )}

        {phase === "ready" && user && (
          <Card data-testid="registration-success-card" className="bg-surface border-emerald-200">
            <CardHeader className="text-center">
              <CheckCircle2 className="mx-auto text-emerald-600 mb-2" size={36} />
              <CardTitle className="text-2xl font-serif text-slate-900">Welcome, {user.full_name?.split(" ")[0] || "Member"}.</CardTitle>
              <CardDescription className="text-platinum/70">
                Your EchoSense Capital Club membership is active.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 rounded bg-blue-50 border border-blue-200 text-sm text-slate-900">
                <p className="font-semibold mb-1">Your EchoSense VC#</p>
                <p className="font-mono text-blue-700">{user.referral_token}</p>
                <p className="text-xs text-slate-600 mt-1">Use this to invite other accredited members.</p>
              </div>
              <Button
                data-testid="goto-command-center-btn"
                onClick={() => navigate("/command-center")}
                className="w-full bg-blue-700 hover:bg-blue-800 text-white"
              >
                Open My Command Center
              </Button>
              <p className="text-xs text-slate-500 leading-relaxed text-center">
                Next: complete your onboarding intake (capital readiness, KYC, accredited investor review).
                EchoSense does not hold client funds or provide investment advice.
              </p>
            </CardContent>
          </Card>
        )}

        {(phase === "failed" || phase === "timeout") && (
          <Card className="bg-surface border-red-200">
            <CardHeader className="text-center">
              <AlertOctagon className="mx-auto text-red-600 mb-2" size={32} />
              <CardTitle className="text-2xl font-serif text-slate-900">
                {phase === "timeout" ? "Still verifying…" : "We couldn't verify your payment"}
              </CardTitle>
              <CardDescription className="text-platinum/70">
                {phase === "timeout"
                  ? "Stripe is taking longer than usual. Refresh this page in a minute, or contact support."
                  : (error || "Please try again or contact support.")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button onClick={() => window.location.reload()} className="w-full bg-slate-900 text-white hover:bg-slate-800">
                Retry Verification
              </Button>
              <Link to="/register" className="block text-center text-sm text-blue-700 hover:underline">
                Back to Registration
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RegistrationSuccessPage;
